import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import RealtimeVoice from '@/lib/realtimeVoice';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Mic, Square, Loader2, X, Volume2, Settings2, Sparkles, Radio, Ear } from 'lucide-react';
import { useBrandName } from '@/lib/branding';
import { toast } from 'sonner';

const VOICE_KEY = 'inzara_voice_name';

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, '').trim();

// Experimental voice for AI Explore. Three ways to talk:
//  • Tap-to-record (Whisper) with LIVE word-by-word transcript.
//  • Hands-Free: say the wake phrase, then INZARA auto-listens for follow-ups.
//  • Barge-In: real-time, interruptible speech-to-speech (OpenAI Realtime).
// Every spoken question runs in the MAIN AI Explore thread (chart renders inline).
export default function VoiceCopilot({ open, onClose, modelId, onAsk, onInvestigate, onAlert, onNavigate, onSave, onSummarize }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [interim, setInterim] = useState('');
  const [turns, setTurns] = useState([]); // {role, text}
  const [voices, setVoices] = useState(['alloy']);
  const [voice, setVoice] = useState(() => localStorage.getItem(VOICE_KEY) || 'alloy');
  const [showSettings, setShowSettings] = useState(false);
  const [clarifyOptions, setClarifyOptions] = useState([]);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [handsFree, setHandsFree] = useState(false);
  const [wakePhrase, setWakePhrase] = useState('Hey INZARA');
  const [rtAvailable, setRtAvailable] = useState(false);
  const [rtOn, setRtOn] = useState(false);
  const [rtStatus, setRtStatus] = useState('idle');

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const srRef = useRef(null);         // Web Speech recognizer (tap-to-record live interim)
  const liveFinalRef = useRef('');
  const hfRef = useRef(null);         // Hands-Free continuous recognizer
  const rtRef = useRef(null);         // RealtimeVoice (Barge-In)
  const openRef = useRef(open);
  const handsFreeRef = useRef(handsFree);
  const rtOnRef = useRef(rtOn);
  const voiceAskRef = useRef(false);  // last ask originated from voice → OK to narrate
  const pendingConfirmRef = useRef(null); // read-back confirmation for destructive actions
  const armedRef = useRef(false);     // hands-free heard the wake phrase → capture the question
  const pausedRef = useRef(false);    // pause hands-free while INZARA is busy/speaking

  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { handsFreeRef.current = handsFree; }, [handsFree]);
  useEffect(() => { rtOnRef.current = rtOn; }, [rtOn]);

  useEffect(() => {
    api.voiceVoices().then((r) => setVoices(r.voices || ['alloy'])).catch(() => {});
    api.voiceConfig().then((c) => {
      if (c?.wake_phrase) setWakePhrase(c.wake_phrase);
      setHandsFree(!!c?.hands_free_default);
      setRtAvailable(!!c?.realtime_available);
    }).catch(() => {});
  }, []);
  useEffect(() => { localStorage.setItem(VOICE_KEY, voice); }, [voice]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, busy, interim]);

  const addTurn = (role, text) => setTurns((t) => [...t, { role, text }]);

  const playAnswer = useCallback(async (text) => {
    if (!text) return;
    try { audioRef.current?.pause(); } catch (_) { /* noop */ }
    try {
      setSpeaking(true);
      const r = await api.voiceSpeak(text, voice);
      const audio = new Audio(`data:audio/mp3;base64,${r.audio_base64}`);
      audioRef.current = audio;
      audio.onended = () => {
        setSpeaking(false);
        // Hands-free: reopen an immediate follow-up window (no wake phrase needed).
        if (handsFreeRef.current && openRef.current && !rtOnRef.current) {
          armedRef.current = true; pausedRef.current = false;
          setTimeout(() => { armedRef.current = false; }, 7000);
        }
      };
      await audio.play();
    } catch (e) { setSpeaking(false); }
  }, [voice]);

  // Play a short greeting in a voice so the user hears what it sounds like.
  const previewVoice = useCallback(async (v) => {
    try {
      const name = v.charAt(0).toUpperCase() + v.slice(1);
      const r = await api.voiceSpeak(`Hi, this is ${name}.`, v);
      const audio = new Audio(`data:audio/mp3;base64,${r.audio_base64}`);
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => setSpeaking(false);
      await audio.play();
    } catch (_) { setSpeaking(false); }
  }, []);

  // Speak the answer once the MAIN thread finishes (chart already rendered there).
  useEffect(() => {
    const onReady = (e) => {
      const speakText = e.detail?.speak || e.detail?.answer;
      setBusy(false);
      pausedRef.current = false;
      setClarifyOptions(Array.isArray(e.detail?.clarify) ? e.detail.clarify : []);
      // Barge-In and tap/hands-free ALL speak AI Explore's exact answer via our
      // TTS — the Realtime model never generates content (no hallucinations).
      if (rtOnRef.current) {
        if (speakText) { addTurn('assistant', speakText); playAnswer(speakText); }
        return;
      }
      // Tap / hands-free: only narrate when open AND the question came from voice.
      if (speakText && openRef.current && voiceAskRef.current) {
        voiceAskRef.current = false;
        addTurn('assistant', speakText);
        playAnswer(speakText);
      }
    };
    window.addEventListener('explore:answer-ready', onReady);
    return () => window.removeEventListener('explore:answer-ready', onReady);
  }, [playAnswer]);

  const submitQuestion = useCallback((text) => {
    if (!text) { toast.message("I didn't catch that — try again"); return; }
    const c = norm(text);
    // Resolve a pending read-back confirmation before anything else.
    if (pendingConfirmRef.current) {
      const pc = pendingConfirmRef.current;
      pendingConfirmRef.current = null; setPendingConfirm(null);
      addTurn('user', text);
      if (/\b(yes|yeah|yep|yup|confirm|do it|go ahead|sure|please do|correct|affirmative|okay|ok)\b/.test(c)) {
        pc.run();
      } else {
        const s = 'Okay, cancelled.'; addTurn('assistant', s); playAnswer(s);
      }
      return;
    }
    addTurn('user', text);
    if (!modelId) {
      const msg = 'Pick a semantic model first, then ask me a question.';
      addTurn('assistant', msg);
      playAnswer(msg);
      return;
    }
    voiceAskRef.current = true;
    pausedRef.current = true;   // stop hands-free capture while INZARA works
    setBusy(true);
    setClarifyOptions([]);
    // Read-back confirm for destructive actions (create/save side effects).
    const requestConfirm = (label, run) => {
      pendingConfirmRef.current = { run, label };
      setPendingConfirm({ label });
      setBusy(false);
      const s = `Just to confirm — you want me to ${label}? Say yes to go ahead, or no to cancel.`;
      addTurn('assistant', s); playAnswer(s);
    };
    // Voice keyword commands (navigation, save, summarize, alert, investigate).
    if (onNavigate) { const dest = onNavigate(text); if (dest) return; }
    if (onSave && c.includes('save') && (c.includes('dashboard') || c.includes('report'))) {
      requestConfirm('save this to a new dashboard', () => onSave());
      return;
    }
    if (onSummarize && (c.includes('summarize') || c.includes('summary') || c.includes('recap'))) { onSummarize(); return; }
    if (onAlert && (c.includes('alert me') || c.includes('create an alert') || c.includes('set an alert') || c.includes('alert if') || c.includes('notify me') || c.includes('let me know if'))) {
      requestConfirm('create a daily alert on this metric', () => onAlert());
      return;
    }
    if (onInvestigate && (c.includes('investigate') || c.includes('root cause') || c.includes('dig into') || c.includes('look into') || c.includes('find out why'))) {
      onInvestigate(text);
      return;
    }
    playAnswer('Analyzing.'); // audible cue while AI Explore is working
    if (onAsk) onAsk(text);     // runs in the MAIN AI Explore thread (chart inline); also handles "forecast this"
  }, [modelId, onAsk, onInvestigate, onAlert, onNavigate, onSave, onSummarize, playAnswer]);

  const resolveConfirm = useCallback((ok) => {
    const pc = pendingConfirmRef.current;
    if (!pc) return;
    pendingConfirmRef.current = null; setPendingConfirm(null);
    if (ok) { addTurn('user', 'Yes'); pc.run(); }
    else { addTurn('user', 'No'); const s = 'Okay, cancelled.'; addTurn('assistant', s); playAnswer(s); }
  }, [playAnswer]);

  // ── Hands-Free (wake phrase + auto follow-up) ─────────────────────────────
  const startHandsFree = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice recognition not supported in this browser'); return; }
    try { hfRef.current?.stop(); } catch (_) { /* noop */ }
    const wp = norm(wakePhrase);
    const sr = new SR();
    sr.continuous = true; sr.interimResults = true; sr.lang = 'en-US';
    sr.onresult = (ev) => {
      if (pausedRef.current) return;
      let interimTxt = '', finalTxt = '';
      for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
        const seg = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalTxt += seg; else interimTxt += seg;
      }
      const combined = norm(`${interimTxt} ${finalTxt}`);
      if (!armedRef.current) {
        if (wp && combined.includes(wp)) {
          armedRef.current = true;
          setInterim('');
          toast.message('Listening…');
        }
        return;
      }
      // Armed — show the question live; submit on a final segment.
      let q = norm(interimTxt || finalTxt);
      if (wp && q.startsWith(wp)) q = q.slice(wp.length).trim();
      setInterim(q);
      if (finalTxt.trim()) {
        let question = finalTxt.trim();
        if (wp) { const idx = norm(question).indexOf(wp); if (idx === 0) question = question.slice(wakePhrase.length).trim(); }
        armedRef.current = false;
        setInterim('');
        if (question) submitQuestion(question);
      }
    };
    sr.onend = () => {
      // Auto-restart the always-on listener while hands-free is enabled.
      if (handsFreeRef.current && openRef.current && !rtOnRef.current) {
        try { sr.start(); } catch (_) { /* noop */ }
      }
    };
    sr.onerror = () => {};
    hfRef.current = sr;
    try { sr.start(); } catch (_) { /* noop */ }
  }, [wakePhrase, submitQuestion]);

  const stopHandsFree = useCallback(() => {
    armedRef.current = false;
    try { hfRef.current?.stop(); } catch (_) { /* noop */ }
    hfRef.current = null;
  }, []);

  // Toggle the always-on hands-free listener when its switch / open state changes.
  useEffect(() => {
    if (open && handsFree && !rtOn) startHandsFree();
    else stopHandsFree();
    return () => stopHandsFree();
  }, [open, handsFree, rtOn, startHandsFree, stopHandsFree]);

  // ── Barge-In (OpenAI Realtime) ────────────────────────────────────────────
  const toggleRealtime = useCallback(async () => {
    if (rtOn || rtRef.current) {
      try { rtRef.current?.stop(); } catch (_) { /* noop */ }
      rtRef.current = null; setRtOn(false); setRtStatus('idle'); setSpeaking(false);
      return;
    }
    stopHandsFree();
    const rt = new RealtimeVoice({
      onStatus: (s) => setRtStatus(s),
      onSpeaking: (s) => setSpeaking(s),
      onBargeIn: () => { try { audioRef.current?.pause(); } catch (_) { /* noop */ } setSpeaking(false); },
      onUserTranscript: (t) => {
        if (!t) return;
        submitQuestion(t); // handles keyword commands, chart inline + spoken answer
      },
    });
    try {
      rtRef.current = rt; setRtOn(true);
      await rt.start();
    } catch (e) {
      toast.error(e?.message || 'Could not start Barge-In voice');
      try { rt.stop(); } catch (_) { /* noop */ }
      rtRef.current = null; setRtOn(false); setRtStatus('idle');
    }
  }, [rtOn, stopHandsFree, submitQuestion]);

  const stopEverything = useCallback(() => {
    try { audioRef.current?.pause(); } catch (_) { /* noop */ }
    setSpeaking(false);
    try { srRef.current?.stop(); } catch (_) { /* noop */ }
    if (mediaRef.current && mediaRef.current.state !== 'inactive') {
      try { mediaRef.current.stop(); } catch (_) { /* noop */ }
    }
    if (mediaRef.current?.stream) mediaRef.current.stream.getTracks().forEach((t) => t.stop());
    setRecording(false);
    setInterim('');
  }, []);
  // On unmount (real navigation away) stop everything including Barge-In.
  useEffect(() => () => { stopEverything(); stopHandsFree(); try { rtRef.current?.stop(); } catch (_) { /* noop */ } }, [stopEverything, stopHandsFree]);
  useEffect(() => {
    if (!open) {
      stopEverything();
      // User explicitly closed the panel → end Barge-In too.
      if (rtRef.current) { try { rtRef.current.stop(); } catch (_) { /* noop */ } rtRef.current = null; setRtOn(false); setRtStatus('idle'); }
    }
  }, [open, stopEverything]);

  // ── Tap-to-record (Whisper, authoritative) with live interim ──────────────
  const startRecording = async () => {
    stopEverything();
    liveFinalRef.current = '';
    setInterim('');
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      try {
        const sr = new SR();
        sr.continuous = true; sr.interimResults = true; sr.lang = 'en-US';
        sr.onresult = (ev) => {
          let text = '';
          for (let i = 0; i < ev.results.length; i += 1) text += ev.results[i][0].transcript;
          liveFinalRef.current = text.trim();
          setInterim(text.trim());
        };
        sr.onerror = () => {};
        srRef.current = sr; sr.start();
      } catch (_) { /* noop */ }
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        try { srRef.current?.stop(); } catch (_) { /* noop */ }
        const liveText = liveFinalRef.current;
        setInterim('');
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 800 && !liveText) { setRecording(false); return; }
        setBusy(true);
        let finalText = liveText;
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'clip.webm');
          const r = await api.voiceTranscribe(fd);
          finalText = (r.text || '').trim() || liveText;
        } catch (e) {
          if (!finalText) toast.error('Transcription failed');
        }
        setBusy(false);
        submitQuestion(finalText);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      try { srRef.current?.stop(); } catch (_) { /* noop */ }
      toast.error('Microphone access denied');
    }
  };

  const brand = useBrandName();
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const _dragStart = useRef(null);
  const onDragMove = useCallback((e) => {
    if (!_dragStart.current) return;
    if (e.touches && e.cancelable) e.preventDefault();
    const p = e.touches ? e.touches[0] : e;
    setDragOffset({ x: _dragStart.current.ox + (p.clientX - _dragStart.current.mx), y: _dragStart.current.oy + (p.clientY - _dragStart.current.my) });
  }, []);
  const onDragEnd = useCallback(() => {
    _dragStart.current = null;
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', onDragEnd);
    window.removeEventListener('touchmove', onDragMove);
    window.removeEventListener('touchend', onDragEnd);
  }, [onDragMove]);
  const onDragStart = useCallback((e) => {
    const p = e.touches ? e.touches[0] : e;
    _dragStart.current = { mx: p.clientX, my: p.clientY, ox: dragOffset.x, oy: dragOffset.y };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
    window.addEventListener('touchmove', onDragMove, { passive: false });
    window.addEventListener('touchend', onDragEnd);
  }, [dragOffset, onDragMove, onDragEnd]);

  if (!open) return null;

  const rtLive = rtOn && rtStatus === 'live';
  return (
    <div className="fixed bottom-40 right-6 z-[60] w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl border border-border/50 bg-card/95 backdrop-blur shadow-2xl overflow-hidden" data-testid="voice-copilot-panel" style={{ transform: (dragOffset.x || dragOffset.y) ? `translate(${dragOffset.x}px, ${dragOffset.y}px)` : undefined }}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/40 bg-primary/5">
        <div className="flex items-center gap-2 text-sm font-heading font-semibold text-foreground cursor-move select-none" onMouseDown={onDragStart} onTouchStart={onDragStart} title="Drag to move" data-testid="voice-drag-handle">
          <Sparkles className="w-4 h-4 text-primary" /> Voice — {brand}
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">Experimental</span>
        </div>
        <div className="flex items-center gap-1">
          {rtAvailable && (
            <button
              onClick={toggleRealtime}
              className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border ${rtOn ? 'bg-red-500/15 text-red-600 dark:text-red-300 border-red-500/40' : 'bg-muted/40 text-muted-foreground border-border/50'}`}
              title="Barge-In — real-time interruptible voice"
              data-testid="voice-realtime-btn"
            >
              <Radio className={`w-3 h-3 ${rtOn ? 'animate-pulse' : ''}`} /> {rtOn ? (rtStatus === 'connecting' ? 'Connecting' : 'Live') : 'Barge-In'}
            </button>
          )}
          <button onClick={() => setShowSettings((s) => !s)} className="text-muted-foreground hover:text-foreground p-1" data-testid="voice-settings-btn"><Settings2 className="w-4 h-4" /></button>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="voice-close-btn"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {showSettings && (
        <div className="px-4 py-3 border-b border-border/40 bg-muted/20 space-y-3">
          <div>
            <label className="text-[11px] text-muted-foreground">Voice</label>
            <select value={voice} onChange={(e) => { const v = e.target.value; setVoice(v); previewVoice(v); }} className="mt-1 w-full h-9 rounded-lg border border-border/50 bg-background px-2 text-sm capitalize" data-testid="voice-select">
              {voices.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <p className="text-[10px] text-muted-foreground/70 mt-1">Pick a voice to hear a quick sample.</p>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[12px] text-foreground"><Ear className="w-3.5 h-3.5 text-primary" /> Hands-free</div>
            <Switch checked={handsFree} onCheckedChange={setHandsFree} data-testid="voice-handsfree-switch" />
          </div>
          {handsFree && <p className="text-[10px] text-muted-foreground/80">Say <span className="font-medium text-foreground">“{wakePhrase}”</span> to start; {brand} auto-listens after each answer.</p>}
          <div className="pt-1 border-t border-border/40">
            <p className="text-[11px] font-medium text-foreground mt-2 mb-1">Try saying…</p>
            <ul className="space-y-0.5 text-[10px] text-muted-foreground/90" data-testid="voice-cheatsheet">
              <li>• “Show me a measure by a dimension this year”</li>
              <li>• “Investigate this” / “Why did it change?”</li>
              <li>• “Forecast this”</li>
              <li>• “Make it a bar chart” / “Add last year”</li>
              <li>• “Summarize this”</li>
              <li>• “Alert me if this changes”</li>
              <li>• “Save this to a dashboard”</li>
              <li>• “Go to investigations” / “Open dashboards”</li>
            </ul>
          </div>
        </div>
      )}

      <div ref={scrollRef} className="max-h-[320px] min-h-[140px] overflow-y-auto px-4 py-3 space-y-2.5" data-testid="voice-transcript">
        {turns.length === 0 && !busy && !interim && (
          <div className="text-center text-muted-foreground text-xs py-8">
            <Mic className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {rtLive ? 'Barge-In is live — just start talking. You can interrupt any time.'
              : handsFree ? `Say “${wakePhrase}”, then ask a question.`
              : 'Tap the mic and ask about your data — e.g. “show me a measure by a dimension.”'}
            <span className="block mt-2 text-[10px] text-muted-foreground/70">Tip: say “investigate this” to run a quick root-cause.</span>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`flex ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${t.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-foreground'}`} data-testid={`voice-turn-${t.role}`}>
              {t.text}
            </div>
          </div>
        ))}
        {interim && (
          <div className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-relaxed bg-primary/40 text-primary-foreground italic" data-testid="voice-interim">{interim}…</div>
          </div>
        )}
        {busy && <div className="flex items-center gap-2 text-muted-foreground text-xs"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</div>}
        {clarifyOptions.length > 0 && !busy && (
          <div className="flex flex-wrap gap-1.5 pt-1" data-testid="voice-clarify-chips">
            {clarifyOptions.map((o, i) => (
              <button
                key={i}
                onClick={() => { setClarifyOptions([]); submitQuestion(o.question || o.label); }}
                className="px-2.5 py-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 text-[11px] hover:bg-indigo-500/20 transition-colors"
                data-testid={`voice-clarify-chip-${i}`}
              >{o.label}</button>
            ))}
          </div>
        )}
        {pendingConfirm && (
          <div className="flex items-center gap-2 pt-1" data-testid="voice-confirm">
            <button onClick={() => resolveConfirm(true)} className="px-3 py-1 rounded-full border border-emerald-500/50 bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 text-[11px] font-medium hover:bg-emerald-500/25" data-testid="voice-confirm-yes">Yes</button>
            <button onClick={() => resolveConfirm(false)} className="px-3 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-300 text-[11px] font-medium hover:bg-red-500/20" data-testid="voice-confirm-no">No, cancel</button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-3 px-4 py-3 border-t border-border/40">
        {speaking && (
          <button
            onClick={() => { try { audioRef.current?.pause(); } catch (_) { /* noop */ } setSpeaking(false); }}
            className="flex items-center gap-1 text-[11px] text-primary" data-testid="voice-stop-speaking"
          >
            <Volume2 className="w-4 h-4 animate-pulse" /> Stop
          </button>
        )}
        {rtOn ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="voice-realtime-status">
            <Radio className={`w-5 h-5 ${rtLive ? 'text-red-500 animate-pulse' : 'text-muted-foreground'}`} />
            {rtStatus === 'connecting' ? 'Connecting…' : rtLive ? 'Live — talk any time' : 'Idle'}
          </div>
        ) : (
          <Button
            onClick={recording ? () => mediaRef.current?.stop() : startRecording}
            disabled={busy || handsFree}
            className={`rounded-full w-14 h-14 p-0 ${recording ? 'bg-red-600 hover:bg-red-700 animate-pulse' : ''}`}
            data-testid="voice-mic-btn"
            title={handsFree ? 'Hands-free is on — say the wake phrase' : (recording ? 'Stop & send' : 'Start speaking')}
          >
            {recording ? <Square className="w-5 h-5" /> : <Mic className="w-6 h-6" />}
          </Button>
        )}
      </div>
    </div>
  );
}
